import { createPortal } from 'react-dom'
import { createRootRouteWithContext, Outlet, useLocation } from '@tanstack/react-router'
import { NuqsAdapter } from 'nuqs/adapters/tanstack-router'
import type { QueryClient } from '@tanstack/react-query'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Toaster } from 'sonner'
import { isSmartQuotePath, useAssistantPanel } from '@/features/quote-builder/assistant/panel-store'
import { DocumentTitle } from '@/components/document-title'

interface RouterContext {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
})

function AgentAwareToaster() {
  // Lift toasts above the SmartQuote launcher when closed, and above its panel when open, so they
  // never overlap the assistant in the bottom-right corner. It only shows in the Quotes section.
  const open = useAssistantPanel((s) => s.open)
  const shown = isSmartQuotePath(useLocation().pathname)
  const bottom = !shown ? '1rem' : open ? 'calc(min(680px, 100dvh - 88px) + 2rem)' : 'calc(2.75rem + 1.75rem)'
  // #root is position: fixed, which traps anything inside it beneath portalled dialogs (z-50). Render
  // toasts on <body> so an error raised from a dialog shows above it instead of under its backdrop.
  return createPortal(
    <Toaster
      position="bottom-right"
      richColors
      offset={{ bottom, right: '1rem' }}
      mobileOffset={{ bottom: '1rem', right: '1rem' }}
    />,
    document.body,
  )
}

function RootComponent() {
  return (
    // List filters live in the URL so Back returns to the filtered view (USA-1358).
    <NuqsAdapter>
      <TooltipProvider>
        <DocumentTitle />
        <Outlet />
        <AgentAwareToaster />
      </TooltipProvider>
    </NuqsAdapter>
  )
}
